declare const L: any;

import type { Ping } from '../../shared/wire';
import { updateElevationPanel, replayClip } from './elevation';
import { escapeHtml } from '../ui/dom';
import { relTime, fmtDateRange } from '../ui/format';
import { deviceDisplayName } from '../ui/animals';
import { groupSessionsLocal, colorForToken } from '../state/cohort-model';
import { getLeafletMap, setLeafletMap, state } from '../state/app-state';
import type { CohortEntry } from '../state/app-state';
import { MOCK } from '../state/device';

/* ─────────────────────────────────────────────
   Tracker map state
   ───────────────────────────────────────────── */
let _tempPingMarker: any = null;
export function getTempPingMarker(): any { return _tempPingMarker; }
export function setTempPingMarker(v: any): void { _tempPingMarker = v; }

let _pendingMapFit = false;
export function getPendingMapFit(): boolean { return _pendingMapFit; }
export function setPendingMapFit(v: boolean): void { _pendingMapFit = v; }

// Callback registered by main.ts boot so tracker.ts can open the device console
// without importing main.ts (breaks the tracker→main cycle). Set via
// setOpenDeviceConsoleCb() before the map is first used.
let _openDeviceConsoleCb: ((token: string, isLocal: boolean) => void) | null = null;
export function setOpenDeviceConsoleCb(fn: (token: string, isLocal: boolean) => void): void { _openDeviceConsoleCb = fn; }

// Callback registered by main.ts so selectSession (in tracker.ts once moved)
// can call renderSessions() without importing main.ts.
let _renderSessionsCb: (() => void) | null = null;
export function setRenderSessionsCb(fn: () => void): void { _renderSessionsCb = fn; }

export let routeLayers: Record<string, any> = {};

/* ─────────────────────────────────────────────
   Helpers
   ───────────────────────────────────────────── */

// Stable colour key for a session from groupSessionsLocal(). Prefer the
// server session_id (stable as new pings arrive); fall back to the
// gap-cluster's start time for null-session runs. The positional id
// ('s0','s1'…) is NOT used — it shifts when a newer session appears, which
// would recolour the whole trail on every new session.
export function sessionColorKey(s: { sessionId: number | null; startT: number }){
  return s.sessionId != null ? `sess:${s.sessionId}` : `gap:${s.startT}`;
}

// Above this, a precision is a non-fix, not a fuzzy area: drawing it as a
// circle just washes the whole map (a real share carries a ~357 km-precision
// garbage fix). The cap sits above the coarsest legitimate tier — quantizeCity
// floors precision at ~11 km — so a privacy-coarsened share still shows its
// honest area, but garbage is dropped. Keep in sync with the Android client's
// MAX_ACCURACY_CIRCLE_M (TrailMap.kt) for parity.
const MAX_ACCURACY_CIRCLE_M = 30_000;

// Draw a faint accuracy circle (radius = precision_meters) so a quantized
// share or a low-accuracy GPS fix reads as a fuzzy area, not a precise
// point. No-op when precision is unknown, implausibly small, or a non-fix
// above MAX_ACCURACY_CIRCLE_M. Returns the circle (or null) so callers can
// fold it into a fitBounds.
export function drawAccuracyCircle(layer: any, p: Ping | undefined, color: string){
  if(!p || p.precision_meters == null || !(p.precision_meters > 1)
     || p.precision_meters > MAX_ACCURACY_CIRCLE_M) return null;
  return L.circle([p.lat, p.lng], {
    radius: p.precision_meters,
    color, weight: 1, opacity: 0.3,
    fillColor: color, fillOpacity: 0.07,
    interactive: false,
  }).addTo(layer);
}

/* ─────────────────────────────────────────────
   Map initialisation
   ───────────────────────────────────────────── */
// getLeafletMap() state moved to state/app-state getLeafletMap/setLeafletMap
export function ensureMap(){
  if(getLeafletMap()){ getLeafletMap().invalidateSize(); return; }
  // Render immediately at a neutral world view; the three-tier rule below
  // snaps to a better center asynchronously so we never block first paint
  // on a Permissions API or getCurrentPosition round-trip.
  setLeafletMap(L.map('map', { zoomControl:true, attributionControl:true, scrollWheelZoom:true })
    .setView([20, 0], 2));
  L.tileLayer('https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png', {
    subdomains:'abcd', attribution:'© OpenStreetMap · CartoDB', maxZoom: 19,
  }).addTo(getLeafletMap());
  drawRoutes();
  pickInitialMapView();
}

// Decide what to center the map on, asynchronously, after the initial
// world-zoom render. Preference order:
//   1. Fit bounds to existing pings.
//   2. getCurrentPosition() if the user has previously granted geolocation
//      (Permissions API check).
//   3. Stay on the world fallback.
export async function pickInitialMapView(){
  if(!getLeafletMap()) return;
  // Share viewer drives its own viewport from the shared trail
  // (shareViewerTick → fitBounds). Never fall through to the viewer's own
  // browser geolocation here: in share mode MOCK.pings is always empty, so
  // the getCurrentPosition() branch below would otherwise win a race
  // against the trail fit and re-centre on the viewer's location.
  if(document.body.dataset.mode === 'share-viewer') return;
  if(MOCK.pings.length > 0){
    const bounds = MOCK.pings.map(p => [p.lat, p.lng]);
    getLeafletMap().fitBounds(bounds, { padding:[20,20], maxZoom: 16 });
    return;
  }
  if(!navigator.geolocation || !navigator.permissions?.query) return;
  try {
    const perm = await navigator.permissions.query({ name: 'geolocation' });
    if(perm.state !== 'granted') return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if(getLeafletMap() && MOCK.pings.length === 0){
          getLeafletMap().setView([pos.coords.latitude, pos.coords.longitude], 15);
        }
      },
      () => { /* swallow; user just sees the world view */ },
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 5_000 }
    );
  } catch { /* Permissions API unsupported on this browser */ }
}

/* ─────────────────────────────────────────────
   Cohort device pin popup
   ───────────────────────────────────────────── */

// Make a cohort device dot clickable (Phase 3): a popup with tag + last-seen and
// a "Console ›" action that opens the device console retargeted to that token.
export function bindDevicePinPopup(marker: any, dev: CohortEntry, isLocal: boolean){
  const name = deviceDisplayName(dev.token, dev.animals, null, undefined, dev.tag);
  const seen = dev.lastT ? relTime(dev.lastT) : '—';
  marker.bindPopup(
    `<div class="team-pin-popup">` +
    `<div class="team-pop-label">${name}</div>` +
    `<div class="team-pop-kind">last seen ${escapeHtml(seen)}</div>` +
    `<button class="menu-action team-pin-console" type="button"><span>${isLocal ? 'Console' : 'View console'} ›</span>` +
    `<i class="ph ph-broadcast" aria-hidden="true"></i></button></div>`
  );
  marker.on('popupopen', (e: any) => {
    const btn = e.popup.getElement()?.querySelector('.team-pin-console') as HTMLElement | null;
    btn?.addEventListener('click', () => { marker.closePopup(); _openDeviceConsoleCb?.(dev.token, isLocal); });
  });
}

/* ─────────────────────────────────────────────
   Route drawing
   ───────────────────────────────────────────── */
export function drawRoutes(opts: { fit?: boolean } = {}){
  // Map may not exist yet (e.g. a boot-time ping hydrate that resolves
  // before show('map')'s deferred ensureMap() runs). Bail rather than
  // throw on null.addLayer — the caller re-draws once the map is up.
  if(!getLeafletMap()) return;
  updateElevationPanel();
  Object.values(routeLayers).forEach(l => getLeafletMap().removeLayer(l));
  routeLayers = {};
  // A focus marker (dropped by tapping a ping/device row) is transient — clear
  // it on any full redraw so it can't linger over a repositioned or
  // live-updating map after the trail underneath it has moved.
  if(getTempPingMarker()){ getLeafletMap().removeLayer(getTempPingMarker()); setTempPingMarker(null); }
  const bounds: any[] = [];

  if(state.viewMode === 'devices'){
    // Cohort mode: one polyline per device, deterministic colour per token,
    // local device retains the #B84A22 accent. No session highlight or
    // popup — clicking a sidebar row pans via focusPing().
    const localToken = MOCK.device?.token;
    for(const dev of state.cohort.values()){
      const isLocal = dev.token === localToken;
      // Viewer-local hide toggle: skip peers the viewer has hidden. The local
      // device is never hideable — it stays the "you are here" anchor.
      if(!isLocal && state.hiddenCohort.has(dev.token)) continue;
      // Local device: draw from MOCK.pings — the live, optimistic source the
      // "By device" sidebar row also reads (renderByDeviceList). The cohort
      // entry is periodically rewound to the server's view by an SSE snapshot
      // (applyCohortSnapshot clears + re-merges), which would momentarily drop
      // a just-recorded — or never-persisted — ping from the map while the
      // sidebar still counts it.
      const pings = isLocal ? MOCK.pings : dev.pings;
      if(!pings || pings.length === 0) continue;
      const coords = pings.map(p => [p.lat, p.lng]);
      const color = isLocal ? '#B84A22' : colorForToken(dev.token);
      const layer = L.layerGroup().addTo(getLeafletMap());
      L.polyline(coords, {
        color, weight: isLocal ? 4 : 3, opacity: 0.85,
        lineCap:'round', lineJoin:'round',
      }).addTo(layer);
      const last = coords[coords.length-1];
      const dot = L.circleMarker(last, {
        radius: isLocal ? 7 : 5, color: '#1F1B16', weight: 1.5,
        fillColor: color, fillOpacity: 1,
      }).addTo(layer);
      bindDevicePinPopup(dot, dev, isLocal);   // click → device console (Phase 3)
      routeLayers['cohort:'+dev.token] = layer;
      coords.forEach(p => bounds.push(p));
    }
    if(opts.fit && bounds.length) getLeafletMap().fitBounds(L.latLngBounds(bounds).pad(0.15));
    return;
  }

  // Sessions / ungrouped modes: per-session render over MOCK.pings using
  // server-driven session_id boundaries for the local device. replayClip
  // hides pings past the replay cutoff (no-op when the scrubber is at the
  // live edge / inactive).
  const sessions = groupSessionsLocal(replayClip(MOCK.pings));
  sessions.forEach(s => {
    const isSel = s.id === state.selectedSession;
    // One colour per session (stable across re-renders); the selected
    // session keeps the local accent so the active pick still pops.
    const color = isSel ? '#B84A22' : colorForToken(sessionColorKey(s));
    const group = L.layerGroup().addTo(getLeafletMap());
    const poly = L.polyline(s.coords, {
      color,
      weight: isSel ? 4 : 2,
      opacity: isSel ? 0.95 : 0.6,
      lineCap:'round', lineJoin:'round'
    }).addTo(group);
    // Honest accuracy circle on the session's latest fix.
    drawAccuracyCircle(group, s.points[s.points.length-1], color);
    const start = s.coords[0], end = s.coords[s.coords.length-1];
    L.circleMarker(start, { radius: isSel? 6:4, color:'#1F1B16', weight:1.5, fillColor:'#F7F3EA', fillOpacity:1 }).addTo(group);
    L.circleMarker(end,   { radius: isSel? 7:5, color:'#1F1B16', weight:1.5, fillColor: color, fillOpacity:1 })
      .bindPopup(`<div class="session-pop">${fmtDateRange(s.startT, s.endT)}<span class="sub">${s.points.length} pings · every ${s.cadence_s}s</span></div>`)
      .addTo(group);
    poly.on('click', ()=> selectSession(s.id));
    routeLayers[s.id] = group;
    s.coords.forEach(p => bounds.push(p));
  });
  // Only fit-bounds on explicit user actions (session click, device switch,
  // boot hydrate). Recording-loop redraws must NOT yank the viewport — the
  // user is panning around their map while pings keep landing.
  if(opts.fit){
    const sel = sessions.find(s=>s.id===state.selectedSession);
    if(sel) getLeafletMap().fitBounds(L.latLngBounds(sel.coords).pad(0.25));
    else if(bounds.length) getLeafletMap().fitBounds(L.latLngBounds(bounds).pad(0.1));
  }
}

export function selectSession(id: string){
  state.selectedSession = id;
  drawRoutes({fit: true});
  _renderSessionsCb?.();
}

/* ─────────────────────────────────────────────
   Focus / pan
   ───────────────────────────────────────────── */
export function focusPing(lat: number, lng: number){
  if(!getLeafletMap()) return;
  if(getTempPingMarker()) getLeafletMap().removeLayer(getTempPingMarker());
  setTempPingMarker(L.circleMarker([lat,lng], {
    radius:9, color:'#B84A22', weight:2, fillColor:'#B84A22', fillOpacity:.55
  }).addTo(getLeafletMap()));
  getLeafletMap().flyTo([lat,lng], Math.max(getLeafletMap().getZoom(), 16), {duration:.5});
}
